import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ComboBox, Input } from '@gokrew/ui';
import {
  countryDisplayName,
  provinceDisplayName,
} from '@gokrew/ui/utility/location';
import { getCrmCountries, getCrmProvinces } from '@/api/crm';
import { useCrmFormat } from './crm-format';
import { CrmCityInput } from './CrmCityInput';

/** Canada first, then the other markets we sell in, then everyone A-Z. */
const PINNED = ['CA', 'AU', 'FR', 'GB', 'US'];
const SEPARATOR = '__separator';
/** Countries whose province or state is picked from a list. */
const WITH_PROVINCES = ['CA', 'US'];

export interface CrmLocation {
  country: string;
  province: string;
  city: string;
}

/**
 * Country, province or state, and city for a CRM company. Values stay the
 * English names the CRM already stores ("Canada", "Quebec"); the lists show
 * them in the reader's language.
 */
export const CrmLocationFields = ({
  value,
  onChange,
}: {
  value: CrmLocation;
  onChange: (next: Partial<CrmLocation>) => void;
}) => {
  const { t, i18n } = useCrmFormat();
  const countries = useQuery({
    queryKey: ['crm', 'countries'],
    queryFn: getCrmCountries,
    staleTime: Infinity,
  });
  const country = (countries.data ?? []).find(
    (c) =>
      c.name.toLowerCase() === value.country.trim().toLowerCase() ||
      countryDisplayName(c, i18n.language).toLowerCase() ===
        value.country.trim().toLowerCase()
  );
  const code = country?.code.toUpperCase() ?? '';
  const provinces = useQuery({
    queryKey: ['crm', 'provinces', code],
    queryFn: () => getCrmProvinces(code),
    enabled: WITH_PROVINCES.includes(code),
    staleTime: Infinity,
  });
  const countryOptions = useMemo(() => {
    const all = (countries.data ?? []).map((c) => ({
      value: c.code.toUpperCase(),
      label: countryDisplayName(c, i18n.language),
    }));
    const collator = new Intl.Collator(i18n.language);
    const pinned = PINNED.map((pin) => all.find((c) => c.value === pin)).filter(
      (c): c is { value: string; label: string } => !!c
    );
    const rest = all
      .filter((c) => !PINNED.includes(c.value))
      .sort((a, b) => collator.compare(a.label, b.label));
    return [
      ...pinned,
      { value: SEPARATOR, label: '──────────', disabled: true },
      ...rest,
    ];
  }, [countries.data, i18n.language]);
  const province = (provinces.data ?? []).find(
    (p) =>
      p.name.toLowerCase() === value.province.trim().toLowerCase() ||
      provinceDisplayName(p, i18n.language).toLowerCase() ===
        value.province.trim().toLowerCase()
  );
  return (
    <>
      <label>
        {t('crm.country')}
        <ComboBox
          size="sm"
          allowUnselect={false}
          value={code}
          placeholder={value.country || t('crm.unknown')}
          searchMode="words"
          options={countryOptions}
          onChange={(next) => {
            const picked = countries.data?.find(
              (c) => c.code.toUpperCase() === next
            );
            if (picked && picked.code.toUpperCase() !== code)
              onChange({ country: picked.name, province: '' });
          }}
        />
      </label>
      <label>
        {t('crm.province')}
        {WITH_PROVINCES.includes(code) ? (
          <ComboBox
            size="sm"
            value={province ? String(province.id) : ''}
            placeholder={value.province || t('crm.unknown')}
            searchMode="words"
            options={(provinces.data ?? []).map((p) => ({
              value: String(p.id),
              label: provinceDisplayName(p, i18n.language),
              keywords: [p.name, p.code],
            }))}
            onChange={(next) =>
              onChange({
                province:
                  provinces.data?.find((p) => String(p.id) === next)?.name ??
                  '',
              })
            }
          />
        ) : (
          <Input
            maxLength={100}
            value={value.province}
            onChange={(event) => onChange({ province: event.target.value })}
          />
        )}
      </label>
      <label>
        {t('crm.city')}
        <CrmCityInput
          value={value.city}
          // Quebec municipalities for now (Canada with Quebec or no province).
          quebec={
            code === 'CA' && (!value.province || province?.code === 'CA-QC')
          }
          onChange={(city, picked) =>
            onChange({
              city,
              ...(picked && !value.province
                ? {
                    province:
                      provinces.data?.find((p) => p.code === 'CA-QC')?.name ??
                      'Quebec',
                  }
                : {}),
            })
          }
        />
      </label>
    </>
  );
};
